import type { Product, BuyRequest } from '@/js/services/RogueTypes'
import type { DimensionOverrides } from '@rogue/rackbuilder'
import type { ProductMeasurementConfig } from '../../../RackTypes'
import { CAVE_6POST_DEPTH_EXTRA, CAVE_CABLE_DEPTH, CAVE_CABLE_WIDTH_OFFSET, CAVE_FOLD_4POST_DEPTH, CAVE_FOLD_6POST_DEPTH, CAVE_INDY_DEPTH, CAVE_PANEL_WIDTH_OFFSET, CAVE_RACK_WIDTH } from './caveConstants'
import { parseRackDepth } from '../../../utils/selection-utils'

function getCavePanelWidth(sideBR: BuyRequest | undefined, products: Record<string, Product>): number | undefined {
  if (!sideBR || sideBR.qty === 0) return undefined

  let br: BuyRequest = sideBR
  while (br.children?.length) br = br.children[0]

  const massWidth = products[String(br.productId)]?.attributes?.mass_width?.toString().replace('"', '')
  if (!massWidth) return undefined

  let width = Number(massWidth) + CAVE_PANEL_WIDTH_OFFSET

  for (const child of sideBR.children ?? []) {
    if (child.qty === 0) continue
    const name = products[String(child.productId)]?.name?.toLowerCase() ?? ''
    for (const [key, offset] of Object.entries(CAVE_CABLE_WIDTH_OFFSET)) {
      if (name.includes(key)) {
        width += offset
        break
      }
    }
  }

  return width
}

function getCaveMainRackProduct(mainBR: BuyRequest | undefined, products: Record<string, Product>): Product | undefined {
  if (!mainBR || mainBR.qty === 0) return undefined

  let br = mainBR
  while (br.children?.length) br = br.children[0]

  return products[String(br.productId)]
}

function getCaveMainRackDepth(mainBR: BuyRequest | undefined, products: Record<string, Product>): number | undefined {
  const product = getCaveMainRackProduct(mainBR, products)
  const rackDepthAttr = product?.attributes?.rack_depth as string | undefined
  if (!rackDepthAttr) return undefined

  const isFixed = product?.name?.toLowerCase().includes('fixed') ?? false

  if (isFixed) {
    const baseDepth = CAVE_INDY_DEPTH[parseRackDepth(rackDepthAttr)]
    if (baseDepth === undefined) return undefined
    const is6Post = String(product?.attributes?.rack_size ?? '').includes('6')
    
    return is6Post ? baseDepth + CAVE_6POST_DEPTH_EXTRA : baseDepth
  }

  // Fold-back: 4-post uses rack_size containing '4', otherwise 6-post
  const is4Post = String(product?.attributes?.rack_size ?? '').includes('4')

  if (is4Post) {
    const depthKey = String(parseFloat(rackDepthAttr)) // '21.5"' → '21.5'
    
    return CAVE_FOLD_4POST_DEPTH[depthKey]
  }

  return CAVE_FOLD_6POST_DEPTH
}


function getCaveSideDepth(sideBR: BuyRequest | undefined, products: Record<string, Product>, mainRackDepth: number | undefined): number | undefined {
  // No side panel configured — fall back to main rack depth so the overlay still shows
  if (!sideBR || sideBR.qty === 0) return mainRackDepth

  const candidates = [
    ...sideBR.children ?? [],
    ...(sideBR.children?.flatMap(c => c.children ?? []) ?? []),
  ]

  // Seed with main rack depth — INDY panel and cable systems may be shallower
  let maxDepth: number | undefined = mainRackDepth

  for (const child of candidates) {
    if (child.qty === 0) continue
    const product = products[String(child.productId)]
    const rackDepthAttr = product?.attributes?.rack_depth as string | undefined
    const name = product?.name?.toLowerCase() ?? ''
    
    // Gets the INDY depth
    if (rackDepthAttr || name.includes('feet')) {
      const depth = CAVE_INDY_DEPTH[parseRackDepth(rackDepthAttr)]

      if (depth !== undefined) {
        maxDepth = maxDepth === undefined ? depth : Math.max(maxDepth, depth)
      }
    }

    // Used to get the rest of the cable system depths
    for (const [key, depth] of Object.entries(CAVE_CABLE_DEPTH)) {
      if (name.includes(key)) {
        maxDepth = maxDepth === undefined ? depth : Math.max(maxDepth, depth)
        break
      }
    }

    // If "None" is selected for a cable system, mini feet are added
    if (name.includes('feet')) {
      maxDepth = mainRackDepth
      break
    }
  }

  return maxDepth
}

export function getCaveDimensionOverrides(buyRequest: BuyRequest, products: Record<string, Product>): DimensionOverrides | null {
  const mainBR  = buyRequest.children?.[0]
  const leftBR  = buyRequest.children?.[1]
  const rightBR = buyRequest.children?.[2]

  const mainRackDepth = getCaveMainRackDepth(mainBR, products)

  const leftPanel  = getCavePanelWidth(leftBR, products)
  const rightPanel = getCavePanelWidth(rightBR, products)
  // Include the rack width whenever at least one panel is configured
  const width = (leftPanel !== undefined || rightPanel !== undefined)
    ? (leftPanel ?? 0) + CAVE_RACK_WIDTH + (rightPanel ?? 0)
    : undefined

  return {
    north : width,
    south : width,
    west  : getCaveSideDepth(leftBR, products, mainRackDepth),
    east  : getCaveSideDepth(rightBR, products, mainRackDepth),
  }
}

export const CAVE_MEASUREMENT_CONFIG: ProductMeasurementConfig = {
  dynamic               : false,
  dynamicOffset         : 0.1,
  dynamicThickness      : 0.001,
  verticalPosition      : 0.02,
  dynamicCapLength      : 0.03,
  defaultZoomLevel      : 2.25,
  getDimensionOverrides : getCaveDimensionOverrides,
}
